import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { LocaleService } from '../../../../core/services/locale.service';
import { PaperIconComponent, PaperButtonComponent } from '@ricoh-paper/angular';

/**
 * AllergenConflictWarningComponent — Warning banner when cart contains user's excluded allergens.
 *
 * Shown in CartSummaryComponent when the cart has items with allergens that the user
 * has marked to avoid in their dietary preferences.
 *
 * Usage:
 * ```html
 * @if (showAllergenWarning()) {
 *   <ct-allergen-conflict-warning
 *     [conflicts]="allergenConflicts()"
 *     (editPreferences)="onEditPreferences()" />
 * }
 * ```
 */
@Component({
  selector: 'ct-allergen-conflict-warning',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PaperIconComponent, PaperButtonComponent, TranslocoPipe],
  template: `
    <div class="acw" role="alert">
      <paper-icon class="acw__icon" name="alert-triangle" [size]="20" />
      <div class="acw__content">
        <strong class="acw__title">{{ 'catering.customer.allergen_warning.title' | transloco }}</strong>
        <p class="acw__message">
          {{ 'catering.customer.allergen_warning.message_prefix' | transloco }} <strong>{{ formatConflicts() }}</strong> {{ 'catering.customer.allergen_warning.message_suffix' | transloco }}
        </p>
      </div>
      <paper-button intent="destructive" size="sm" (click)="editPreferences.emit()">
        {{ 'catering.customer.allergen_warning.edit_preferences' | transloco }}
      </paper-button>
    </div>
  `,
  styles: [`
    .acw {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      padding: 12px 14px;
      background: var(--error-bg);
      border: 1px solid color-mix(in srgb, var(--danger) 30%, transparent);
      border-radius: var(--radius-sm, 8px);
      margin-bottom: 16px;
    }

    .acw__icon {
      flex-shrink: 0;
      color: var(--error-text);
    }

    .acw__content {
      flex: 1;
      min-width: 0;
    }

    .acw__title {
      display: block;
      font-size: 13px;
      font-weight: 600;
      color: var(--error-text);
      margin: 0 0 4px;
    }

    .acw__message {
      font-size: 12px;
      line-height: 1.4;
      color: var(--text-secondary);
      margin: 0;
    }

    .acw__message strong {
      font-weight: 600;
    }

    paper-button {
      flex-shrink: 0;
    }

    @media (max-width: 480px) {
      .acw {
        flex-direction: column;
        gap: 10px;
      }

      paper-button {
        align-self: flex-start;
      }
    }
  `],
})
export class AllergenConflictWarningComponent {
  /** Array of allergen names that conflict with user preferences */
  readonly conflicts = input.required<string[]>();

  /** Emitted when user clicks "Edit preferences" */
  readonly editPreferences = output<void>();

  private readonly transloco = inject(TranslocoService);
  private readonly locale = inject(LocaleService);

  /** Format allergen list for display (locale-aware join) */
  protected formatConflicts(): string {
    this.locale.locale();
    const conflicts = this.conflicts();
    if (conflicts.length === 0) return '';
    if (conflicts.length === 1) return conflicts[0];
    const and = this.transloco.translate('common.list.and');
    const sep = this.transloco.translate('common.list.separator');
    if (conflicts.length === 2) return conflicts.join(` ${and} `);
    const last = conflicts[conflicts.length - 1];
    const rest = conflicts.slice(0, -1);
    return rest.join(sep) + sep + and + ' ' + last;
  }
}
